<template>
    <div v-if="retailer !=undefined" class="w-full h-full">
        <Header
            title="Retailer"
            icon="Store"
        />
        <Breadcrumbs text1="retailer-catalogue" link1="/retailer-catalogue" :text2="retailer.retailerName"
            :link2='`${retailerId}`'>
        </Breadcrumbs>

        <SubHeader :logo="retailer.logo" :title="retailer.retailerName" :subText="retailer.personalisationCategories == null ?  '' : retailer.personalisationCategories[0]" icon="Store">
            <template v-slot:buttons>
                <a v-if="formattedRetailerWebsite != null" class="" :href="formattedRetailerWebsite" target=”_blank”>
                    <CustomButton id="website" small text="Website" icon="ExternalLink" type="navigate" />
                </a>
            </template>
        </SubHeader>

        <WhiteBox title="Your offers" subText="View the offers from our suppliers">
            <template v-slot:content>
                <EasyDataTable
                    :headers="activatedOffersTableHeader"
                    :items="activatedOffers"
                    :loading="loading"
                    v-model:server-options="serverOptions"
                    :server-items-length="serverItemsLength"
                    :hide-footer="false"
                    :no-hover="true"
                >
                    <template #loading>
                        <div class="p-4 w-full flex justify-center">
                            <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    </template>
                    <template #empty-message>
                    <div class="flex justify-center py-4">
                        <div class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                            <p class="text-sm mr-2">No results</p>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                            </svg>
                        </div>
                    </div>
                    </template>
                    
                    <template #item-retailer="retailers">
                        <LogoAndText 
                            :logo="retailers.retailer.logo"
                            :text="retailers.retailer.retailerName"
                        />
                    </template>
                    <template #item-supplier="retailers">
                        <Supplier 
                            :supplier="retailers.offer.supplier"
                        />
                    </template>
                    <template #item-offertype="retailers">
                        <OfferTypePill
                            :type="retailers.offer.offerType"
                        />
                    </template>
                    <template #item-cashbackAmount="retailers">
                        <CashbackPill
                            :text="retailers.displayRules[0].highestCashbackPercentageAmount"
                            :type="retailers.cashbackEarnings[0].conditions[0].value.type"
                            :currency="retailers.offer.currency"
                        />
                    </template>
                    <template #item-active="retailer">
                        <StatusPill
                            v-if="retailer.active"
                            text="Live"
                            colour="green"
                        />
                        <StatusPill
                            v-else
                            text="Deactivated"
                            colour="yellow"
                        />
                    </template>
                    <template #item-offerApproval="retailer">
                        <div v-if="retailer.offerApproval != undefined">
                        <StatusPill
                            v-if="retailer.offerApproval === 'APPROVED'"
                            text="Approved"
                            colour="green"
                        />
                        <StatusPill
                            v-if="retailer.offerApproval === 'PENDING'"
                            text="Pending approval"
                            colour="yellow"
                        />
                        <StatusPill
                            v-if="retailer.offerApproval === 'DECLINED'"
                            text="Approval declined"
                            colour="red"
                        />
                    </div>
                    </template>
                    <template #header-action="header">
                        <div class="w-full text-right">
                            {{ header.text }}
                        </div>
                    </template>
                    <template #item-action="retailers">
                        <div class="flex justify-end items-center py-1 gap-1">
                            <CustomButton small text="View Offer" type="navigate" @click="$router.push(`/retailer-catalogue/${retailers.retailer.id}/offer/${retailers.offer.id}`)" />
                        </div>
                    </template>
                </EasyDataTable>
            </template>
        </WhiteBox>

        <WhiteBox title="Offers avaliable" subText="View the offers from our suppliers">
            <template v-slot:content>
                <EasyDataTable :headers="headersTable1" :items="finalOffersAvalaible" :loading="loading" :hide-footer="false"
                    :no-hover="true">
                    <template #loading>
                        <div class="p-4 w-full flex justify-center">
                            <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                        </div>
                    </template>
                    <template #empty-message>
                        <div class="flex justify-center py-4">
                            <div
                                class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                <p class="text-sm mr-2">No results</p>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                </svg>
                            </div>
                        </div>
                    </template>
                    
                    <template #item-retailer="retailers">
                        <LogoAndText 
                            :logo="retailers.retailer.logo"
                            :text="retailers.retailer.retailerName"
                        />
                    </template>
                    <template #item-supplier="retailers">

                        <Supplier 
                            :supplier="retailers.supplier"
                        />
                    </template>
                    <template #item-offertype="retailers">
                        <OfferTypePill
                            :type="retailers.offerType"
                        />
                    </template>
                    <template #item-cashbackAmount="retailers">
                        <CashbackPill
                            :text="retailers.cashbackRules[0].conditions[0].value.maxAvailableValue"
                            :type="retailers.cashbackRules[0].conditions[0].value.type"
                            :currency="retailers.currency"
                        />
                    </template>
                    <template #header-action="header">
                        <div class="w-full text-right">
                            {{ header.text }}
                        </div>
                    </template>
                    <template #item-action="retailers">
                        <div class="flex justify-end items-center py-1 gap-1">
                            <CustomButton small text="View Offer" type="navigate" @click="$router.push(`/retailer-catalogue/${retailers.retailer.id}/offer/${retailers.id}`)" />
                        </div>
                    </template>
                </EasyDataTable>
            </template>
        </WhiteBox>
    </div>
</template>

<script setup>
</script>

<script>
import Header from "@/components/Header.vue";
import SubHeader from "@/components/SubHeader.vue";
import WhiteBox from "@/components/WhiteBox.vue";
import Breadcrumbs from "@/components/navbars/Breadcrumbs.vue";
import CustomButton from "@/components/inputs/CustomButton.vue";
import LogoAndText from "@/components/LogoAndText.vue";
import StatusPill from "@/components/StatusPill.vue";
import CashbackPill from "@/components/CashbackPill.vue";
import OfferTypePill from "@/components/OfferTypePill.vue";
import Supplier from "@/components/Supplier.vue";

export default {
    name: "manage-publsihers",
    components: {
        Header,
        SubHeader,
        WhiteBox,
        Breadcrumbs,
        CustomButton,
        LogoAndText,
        StatusPill,
        CashbackPill,
        OfferTypePill,
        Supplier
    },
    props: {
    },

    data() {
        return {
            retailerId: this.$route.params.retailerId,
            retailer:undefined,

            showErrorModal: false,
            showSuccessModal: false,
            showFormModal: false,
            loading: true,

            headersTable1: [
                { text: "Retailer", value: "retailer" },
                { text: "Supplier", value: "supplier"},
                { text: "Offer type", value: "offertype"},
                { text: "Cashback amount", value: "cashbackAmount"},                  
                { text: "Action", value: "action"},
            ],

            activatedOffersTableHeader: [
                { text: "Retailer", value: "retailer" },
                { text: "Supplier", value: "supplier"},
                { text: "Offer type", value: "offertype"},
                { text: "Cashback amount", value: "cashbackAmount"},                  
                { text: "Availability status", value: "active"},
                { text: "Approval status", value: "offerApproval"},
                { text: "Action", value: "action"},
            ],
            offerIds:undefined,

            offers: [

            ],
            activatedOffers:[]
        }
    },
    created() {
        this.getRetailer()
        this.getActivatedOffers(0,50)

    },
    methods: {

        async getRetailer() {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')



                const response = await fetch(`https://${api}.boogi.io/private/api/client/catalogue/retailer/${this.retailerId}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.offerIds = object.offers
                    this.retailer = object
                    this.getOffers(this.offerIds)
                    
                }

            } catch (error) {
                return;
            }

        },

        async getOffers(offerIds) {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')

                const response = await fetch(`https://${api}.boogi.io/private/api/client/catalogue/offer-by-ids?offerId=${offerIds}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.offers = object

                    this.loading = false
                }

            } catch (error) {
                return;
            }

        },



        async getActivatedOffers(page, size) {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')

                const response = await fetch(`https://${api}.boogi.io/private/api/client/activated-offer/retailer/${this.retailerId}?page=${page}&size=${size}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.activatedOffers = object.content

                    this.loading = false
                }

            } catch (error) {
                return;
            }

        },

    },
    computed: {
        finalOffersAvalaible(){

            var x = this.offers

            return x
        },
        formattedRetailerWebsite() {

            if(this.retailer.retailerWebsite == null)
            {
                return null
            }

            // Check if retailerWebsite starts with "https://" or "http://"
            if (this.retailer.retailerWebsite.startsWith('https://') || this.retailer.retailerWebsite.startsWith('http://')) {
                return this.retailer.retailerWebsite;
            } else {
                // If it doesn't, add "https://" prefix
                return `https://${this.retailer.retailerWebsite}`;
            }
        }

    },
    mounted() {
        document.title = "Dashboard | Retailer";
    },
    watch: {
    }
}
</script>